/**
 * Room status: Proposed → Approved → Completed (migrations 0016, 0026, 0027).
 *
 * Extracted rather than inlined into the templates because the same "Approved
 * — <date>" string renders on the room card, the room page, and the client and
 * contractor portals. Four copies of a conditional is how the date-formatting
 * bug got in last time.
 *
 * ─── THE LIFECYCLE ──────────────────────────────────────────────────────────
 * This was a two-state toggle until 2026-09-18, when Mick asked for Completed
 * back (reversing decisions log #4). The rules, all chosen that day:
 *
 *  - The designer marks a room Completed by hand. Data informs the decision —
 *    see roomReadiness() — but never makes it: "finished" includes styling and
 *    a final walkthrough, which no spec status captures.
 *  - A room must be Approved before it can be Completed. Enforced in the
 *    database trigger too, so this module is not the only line of defense.
 *  - Completing a room KEEPS its approval record. The previous trigger erased
 *    it on any move away from 'approved' — which is why approvalStamp() and
 *    approvalDetail() below now treat 'completed' as approved-and-beyond.
 */

import { fmtDate, esc } from '@/lib/dom';
import type { RoomStatus, SpecRow, PunchItemRow, ScheduleEntryRow } from '@/types/database';

/** The subset of a room this module needs — portals pass a narrower shape. */
export interface RoomStatusFields {
  status: RoomStatus;
  /** timestamptz. Optional: older portal payloads don't carry it. */
  approved_at?: string | null;
  /** timestamptz. Optional: payloads from before 0027 don't carry it. */
  completed_at?: string | null;
}

/**
 * True for a room the client has signed off on — including one that has since
 * been completed.
 *
 * The single reason this exists: every "is this room approved?" check used to
 * be `status === 'approved'`, and with a third status each of those silently
 * started reporting a completed room as never having been approved.
 */
export function hasApproval(status: RoomStatus): boolean {
  return status === 'approved' || status === 'completed';
}

/**
 * Trailing stamp for an approved room, or `null` when there is nothing to show.
 *
 * Returns null — rather than a placeholder — when the room was never approved,
 * the approval predates the column, or the payload omits the field. Callers
 * render nothing.
 */
export function approvalStamp(room: RoomStatusFields): string | null {
  if (!hasApproval(room.status)) return null;
  if (!room.approved_at) return null;
  // approved_at is a timestamptz, so fmtDate localizes it to the viewer —
  // correct here, unlike the date-only columns (see @/lib/dates).
  const formatted = fmtDate(room.approved_at);
  return formatted === '—' ? null : `Approved ${formatted}`;
}

/** "Completed <date>", or null for a room that isn't completed. */
export function completionStamp(room: RoomStatusFields): string | null {
  if (room.status !== 'completed') return null;
  if (!room.completed_at) return 'Completed';
  const formatted = fmtDate(room.completed_at);
  return formatted === '—' ? 'Completed' : `Completed ${formatted}`;
}

/** The approval record on a room: who, when, and how it was obtained. */
export interface RoomApprovalFields extends RoomStatusFields {
  approved_by_name?: string | null;
  /** 0040. Older portal payloads don't carry it. */
  approved_via?: 'portal' | 'designer' | null;
  approval_note?: string | null;
}

/**
 * Full approval line: who approved, when, and how it was obtained.
 *
 * Distinct from approvalStamp() in that it names the signer and says where the
 * approval came from. Until 0040 the absence of a name was what said "the
 * designer set this themselves" — a fact recorded only by omission, and
 * omission is not a record. Now both paths sign, and `approved_via` says which
 * is which; a room approved before 0040 has neither, and reads as it always
 * did.
 *
 * Shown wherever the approval is — including on a completed room, whose
 * approval is exactly as binding as it was before completion.
 */
export function approvalDetail(room: RoomApprovalFields): string | null {
  if (!hasApproval(room.status)) return null;
  const who = room.approved_by_name ? ` by ${room.approved_by_name}` : '';
  const stamp = approvalStamp(room);
  const when = stamp ? ` on ${stamp.replace(/^Approved /, '')}` : '';
  const how = room.approved_via === 'designer'
    ? ` · recorded by the designer${room.approval_note ? `: ${room.approval_note}` : ''}`
    : room.approved_via === 'portal' ? ' · signed in the client portal' : '';
  if (!who && !when && !how) return 'Approved';
  return `Approved${who}${when}${how}`;
}

/**
 * Warning shown before withdrawing an approval, or null when none is needed.
 *
 * Withdrawing destroys the approval record — the one thing that settles a
 * later dispute — so anything with a record behind it warns. A room approved
 * before 0040, by a designer who was never asked for a name, has no record to
 * destroy and so needs no warning.
 */
export function withdrawalWarning(room: RoomApprovalFields): string | null {
  if (room.status !== 'approved') return null;
  if (!room.approved_by_name) return null;
  const detail = approvalDetail(room);
  return `${detail}. Withdrawing will remove that approval record.`;
}

// ─── Transitions ──────────────────────────────────────────────────────────

export interface StatusAction {
  to: RoomStatus;
  label: string;
  /** The forward step renders as the primary button. */
  primary: boolean;
}

/**
 * What the designer can do from here, in display order.
 *
 * Deliberately never offers Proposed → Completed: the database refuses it, so
 * offering it would only produce an error. Reopening goes to Approved rather
 * than Proposed, because a finished room being reopened still has its approval
 * — dropping it would be a second, unrelated decision. Withdrawing after that
 * is one more click, and says so.
 */
export function roomStatusActions(status: RoomStatus): StatusAction[] {
  switch (status) {
    case 'proposed':
      return [{ to: 'approved', label: 'Mark approved', primary: true }];
    case 'approved':
      return [
        { to: 'completed', label: 'Mark completed', primary: true },
        { to: 'proposed', label: 'Withdraw approval', primary: false },
      ];
    case 'completed':
      return [{ to: 'approved', label: 'Reopen room', primary: false }];
  }
}

// ─── The client portal ────────────────────────────────────────────────────

export interface ClientRoomControls {
  /** Offer "I approve this room". */
  canApprove: boolean;
  /**
   * Requesting a change will withdraw an approval the client already gave, so
   * the portal should say so before they type.
   */
  changeWithdrawsApproval: boolean;
}

/**
 * What a client may do to a room from their share link.
 *
 * Extracted from share-view because the template used to branch on
 * `status === 'approved'` alone, which with a third status sent a COMPLETED
 * room down the else branch — offering "I approve this room" on finished work.
 * The server refuses that now (0027), so the client would have got a
 * misleading "link may have expired" error instead.
 *
 * A completed room still takes feedback, but the status holds (chosen
 * 2026-09-18), so it must NOT carry the "this will withdraw your approval"
 * warning — that warning would be false.
 */
export function clientRoomControls(status: RoomStatus): ClientRoomControls {
  switch (status) {
    case 'proposed':  return { canApprove: true,  changeWithdrawsApproval: false };
    case 'approved':  return { canApprove: false, changeWithdrawsApproval: true };
    case 'completed': return { canApprove: false, changeWithdrawsApproval: false };
  }
}

// ─── Readiness ────────────────────────────────────────────────────────────

/**
 * How finished a room looks from the data — shown beside "Mark completed".
 *
 * INFORMATIVE, never blocking (chosen 2026-09-18). Spec statuses are only as
 * current as the designer keeps them, so a hard gate would lock a genuinely
 * finished room behind a material she never bothered to flip to Installed.
 */
export interface RoomReadiness {
  materialsInstalled: number;
  /** Excludes rejected materials — those were never going to be installed. */
  materialsTotal: number;
  openPunch: number;
  punchTotal: number;
  openTasks: number;
  tasksTotal: number;
  /** Nothing outstanding. */
  ready: boolean;
  /**
   * Whether anything is tracked for this room at all.
   *
   * A room with no materials, no punch items and no tasks is vacuously
   * "ready" — nothing is outstanding because nothing exists. Reporting that as
   * a green "ready to complete" would claim a verdict the data can't support,
   * so callers show "nothing tracked" instead.
   */
  tracked: boolean;
}

/** Schedule statuses that mean a task needs nothing more. */
const DONE_TASK = new Set(['complete', 'skipped']);

/**
 * Callers pass lists already scoped to ONE room. The room page loads them that
 * way; filtering here as well would hide a caller that passed the wrong room's
 * data behind a count that merely looks plausible.
 */
export function roomReadiness(
  specs: Pick<SpecRow, 'status'>[],
  punch: Pick<PunchItemRow, 'complete'>[],
  tasks: Pick<ScheduleEntryRow, 'status'>[],
): RoomReadiness {
  const live = specs.filter((s) => s.status !== 'rejected');
  const materialsInstalled = live.filter((s) => s.status === 'installed').length;
  const openPunch = punch.filter((p) => !p.complete).length;
  const openTasks = tasks.filter((t) => !DONE_TASK.has(t.status)).length;
  const materialsTotal = live.length;

  return {
    materialsInstalled,
    materialsTotal,
    openPunch,
    punchTotal: punch.length,
    openTasks,
    tasksTotal: tasks.length,
    ready: materialsInstalled === materialsTotal && openPunch === 0 && openTasks === 0,
    tracked: materialsTotal + punch.length + tasks.length > 0,
  };
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** The checklist lines, one per tracked category. Untracked categories are omitted. */
export function readinessLines(r: RoomReadiness): string[] {
  const lines: string[] = [];
  if (r.materialsTotal > 0) {
    lines.push(`${r.materialsInstalled} of ${plural(r.materialsTotal, 'material', 'materials')} installed`);
  }
  if (r.punchTotal > 0) {
    lines.push(r.openPunch === 0 ? 'Punch list clear' : `${plural(r.openPunch, 'open punch item', 'open punch items')}`);
  }
  if (r.tasksTotal > 0) {
    lines.push(r.openTasks === 0 ? 'All tasks done' : `${plural(r.openTasks, 'open task', 'open tasks')}`);
  }
  return lines;
}

/**
 * Confirmation to show before completing a room with work still outstanding,
 * or null to complete without asking.
 *
 * A nudge, not a gate: she can always say yes. It exists so an outstanding
 * punch item is a decision rather than an oversight.
 */
export function completionWarning(r: RoomReadiness): string | null {
  if (r.ready) return null;
  const outstanding: string[] = [];
  const notInstalled = r.materialsTotal - r.materialsInstalled;
  if (notInstalled > 0) outstanding.push(plural(notInstalled, 'material not yet installed', 'materials not yet installed'));
  if (r.openPunch > 0) outstanding.push(plural(r.openPunch, 'open punch item', 'open punch items'));
  if (r.openTasks > 0) outstanding.push(plural(r.openTasks, 'open task', 'open tasks'));
  return `This room still has ${outstanding.join(', ')}. Mark it completed anyway?`;
}

/**
 * The readiness checklist beside "Mark completed", as markup.
 *
 * Lives here rather than in room-detail because it's a pure function of the
 * readiness — and the one decision in it that matters (an untracked room says
 * so rather than claiming "ready") is exactly the kind a view hides from tests.
 * Informative only: nothing about it disables the button.
 */
export function readinessMarkup(r: RoomReadiness): string {
  if (!r.tracked) {
    return `<div class="room-readiness muted">Nothing tracked for this room yet.</div>`;
  }
  const lines = readinessLines(r).map((l) => `<li>${esc(l)}</li>`).join('');
  return `<div class="room-readiness${r.ready ? ' is-ready' : ''}">`
    + (r.ready ? `<div class="room-readiness-verdict">Ready to complete</div>` : '')
    + `<ul>${lines}</ul></div>`;
}
